import {
  PieChart,
  Pie,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  ScatterChart,
  Scatter,
  Cell,
} from "recharts";
import ChartToolTip from "../ChartToolTip/ChartToolTip";
import {
  prepareSliderDataGroupedByRangeBins,
  prepareDualCounterData,
  prepareMultisliderData,
  prepareDefaultData,
  calculateMarginBottom,
  generateShades,
} from "../../utils/chartUtils";

const CHART_CONFIG = {
  height: 250,
  margin: { top: 20, right: 30, left: 0, bottom: 40 },
  fontSize: {
    small: 10,
    medium: 12,
    large: 16,
  },
};

// Common chart wrapper component - no styling since parent provides it
const ChartContainer = ({ children, className = "" }) => (
  <div className={className}>
    <ResponsiveContainer width="100%" height={CHART_CONFIG.height}>
      {children}
    </ResponsiveContainer>
  </div>
);

// Slider chart component
const SliderChart = ({ data, question, index }) => {
  const chartData = prepareSliderDataGroupedByRangeBins(
    data,
    question.min ?? 0,
    question.max ?? 100
  );

  const hue = (index * 70) % 360;
  const colors = generateShades(hue, chartData.length, "luminosity");

  const enrichedChartData = chartData.map((entry, i) => ({
    ...entry,
    index: i,
  }));

  return (
    <ChartContainer>
      <PieChart>
        <Tooltip
          content={({ payload }) => {
            if (!payload || payload.length === 0) return null;

            const sliceIndex = payload[0].payload.index;
            const color = colors[sliceIndex] || "#8884d8";

            return (
              <ChartToolTip
                type="slider"
                payload={[
                  {
                    ...payload[0],
                    color,
                    payload: {
                      ...payload[0].payload,
                      unit: question.unit || "",
                    },
                  },
                ]}
              />
            );
          }}
        />
        <Pie
          data={enrichedChartData}
          dataKey="value"
          nameKey="label"
          cx="50%"
          cy="50%"
          outerRadius={80}
          label={({ name }) =>
            `${name}${question.unit ? ` ${question.unit}` : ""}`
          }
        >
          {colors.map((fill, i) => (
            <Cell key={`cell-${i}`} fill={fill} />
          ))}
        </Pie>
      </PieChart>
    </ChartContainer>
  );
};

// Dual counter chart component
const DualCounterChart = ({ data, index }) => {
  const chartData = prepareDualCounterData(data);

  const allX = chartData.map((d) => d.x);
  const allY = chartData.map((d) => d.y);

  const minX = Math.min(...allX);
  const maxX = Math.max(...allX);
  const minY = Math.min(...allY);
  const maxY = Math.max(...allY);

  return (
    <ChartContainer>
      <ScatterChart margin={CHART_CONFIG.margin}>
        <XAxis
          type="number"
          dataKey="x"
          name="Auto"
          domain={[minX, maxX]}
          tickCount={maxX - minX + 1}
          interval={0}
          tick={{ fontSize: CHART_CONFIG.fontSize.medium }}
          label={{ value: "Auto", position: "bottom", offset: 0 }}
        />
        <YAxis
          type="number"
          dataKey="y"
          name="Guidatori"
          tickCount={maxY - minY + 1}
          domain={[minY, maxY]}
          tick={{ fontSize: CHART_CONFIG.fontSize.medium }}
          label={{
            value: "Guidatori",
            angle: -90,
            position: "insideLeft",
          }}
        />
        <ZAxis
          type="number"
          dataKey="z"
          range={[60, 400]}
          name="Risposte"
        />
        <Tooltip content={<ChartToolTip type="dual_counter" />} />
        <Scatter
          data={chartData}
          fill={`hsl(${(index * 70) % 360}, 70%, 70%)`}
        />
      </ScatterChart>
    </ChartContainer>
  );
};

// Multislider chart component
const MultisliderChart = ({ data, index }) => {
  const chartData = prepareMultisliderData(data);
  const marginBottom = calculateMarginBottom(chartData);

  return (
    <ChartContainer>
      <BarChart
        data={chartData}
        margin={{ ...CHART_CONFIG.margin, bottom: marginBottom }}
      >
        <XAxis
          dataKey="truncated"
          interval={0}
          angle={-20}
          textAnchor="end"
          tick={{ fontSize: CHART_CONFIG.fontSize.medium }}
        />
        <YAxis
          allowDecimals={true}
          domain={[0, 100]}
          tickFormatter={(val) => `${val.toFixed(0)}%`}
        />
        <Tooltip content={<ChartToolTip type="multislider" />} />
        <Bar dataKey="value" fill={`hsl(${(index * 70) % 360}, 70%, 70%)`}>
          <LabelList
            dataKey="value"
            position="top"
            formatter={(val) => `${val.toFixed(2)}%`}
          />
        </Bar>
      </BarChart>
    </ChartContainer>
  );
};

// Default bar chart component (for buttons and profiling)
const DefaultBarChart = ({ data, question, index }) => {
  const chartData = prepareDefaultData(data, question);
  const marginBottom = calculateMarginBottom(chartData);

  return (
    <ChartContainer>
      <BarChart
        data={chartData}
        margin={{ ...CHART_CONFIG.margin, bottom: marginBottom }}
      >
        <XAxis dataKey="truncated" angle={-20} textAnchor="end" />
        <YAxis allowDecimals={false} />
        <Tooltip content={<ChartToolTip />} />
        <Bar dataKey="count" fill={`hsl(${(index * 70) % 360}, 70%, 60%)`}>
          <LabelList dataKey="count" position="top" />
        </Bar>
      </BarChart>
    </ChartContainer>
  );
};

// Main component with chart type routing
export default function QuestionChart({ type, question, data, index = 0 }) {
  const chartProps = { data, question, index };

  switch (type) {
    case "slider":
      return <SliderChart {...chartProps} />;
    
    case "dual_counter":
      return <DualCounterChart {...chartProps} />;
    
    case "multislider":
      return <MultisliderChart {...chartProps} />;
    
    case "buttons":
    case "profiling":
    default:
      return <DefaultBarChart {...chartProps} />;
  }
}